import React, { useState, useRef, useEffect } from 'react';
import { View, Text, StyleSheet, SafeAreaView, TextInput, TouchableOpacity, ScrollView, KeyboardAvoidingView, Platform, Linking, Alert } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useThemeColors, SIZES, Colors } from '../constants/theme';
import { firestoreService } from '../services/firestoreService';
import { useAppStore } from '../store/useAppStore';
import PantallaSegura, { useMargenesSeguros } from '../components/PantallaSegura';
import AccionesDeChat, { AvisoDeBloqueo } from '../components/AccionesDeChat';

export default function ChatScreen() {
  const router = useRouter();
  const params = useLocalSearchParams();
  const colors = useThemeColors();
  const scrollViewRef = useRef<ScrollView>(null);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  /**
   * Espejo del texto que nunca va por detrás del estado de React.
   * Ver la explicación completa en `handleSend`: en Android el teclado puede
   * tener la última palabra sin confirmar cuando se toca Enviar.
   */
  const textoRef = useRef('');
  const inputRef = useRef<TextInput>(null);

  /** Único punto por el que cambia el texto: mantiene estado y ref a la par. */
  const alEscribir = (t: string) => {
    textoRef.current = t;
    setMessage(t);
  };

  // ⚠️ FIX CRÍTICO DE SEGURIDAD (auditoría) — Esta pantalla era MUERTA: guardaba
  // los mensajes en useState y NUNCA los enviaba. Una pasajera podía escribir
  // "me siento incómoda" durante un viaje real, ver su mensaje en pantalla, y
  // ese mensaje jamás salía de su teléfono. Ahora se envía y se lee de verdad
  // desde /rides/{rideId}/messages.
  const rideId = String((params as any)?.rideId ?? '').trim();
  const partnerName = String((params as any)?.partnerName ?? '').trim() || 'Chofer';
  const myUid = useAppStore((s) => s.userFirebaseUid);
  const [messages, setMessages] = useState<any[]>([]);

  /**
   * QUIÉN ES LA OTRA PERSONA. Hace falta para bloquearla o reportarla, y esta
   * pantalla solo recibía su NOMBRE por parámetro. Se saca del viaje: el que
   * no soy yo. Una lectura, al abrir.
   */
  const [otroUid, setOtroUid] = useState<string | null>(null);
  const [chatBloqueado, setChatBloqueado] = useState(false);
  useEffect(() => {
    let vivo = true;
    if (!rideId || !myUid) return;
    firestoreService
      .getRideById(rideId)
      .then((r: any) => {
        if (!vivo || !r) return;
        const otro = String(r.driverId ?? '') === String(myUid)
          ? String(r.passengerId ?? '')
          : String(r.driverId ?? '');
        if (otro) {
          setOtroUid(otro);
          firestoreService
            .hayBloqueoEntre(String(myUid), otro)
            .then((b) => { if (vivo) setChatBloqueado(!!b); })
            .catch(() => {});
        }
      })
      .catch(() => {});
    return () => { vivo = false; };
  }, [rideId, myUid]);

  const [cargandoChat, setCargandoChat] = useState(true);
  const [errorChat, setErrorChat] = useState<string | null>(null);

  // Escuchar la conversación real en vivo.
  useEffect(() => {
    if (!rideId) { setCargandoChat(false); return; }
    setCargandoChat(true);
    setErrorChat(null);
    const unsub = firestoreService.listenToRideMessages(
      rideId,
      (docs) => {
        setCargandoChat(false);
        setErrorChat(null);
        setMessages(
          (docs ?? []).map((m: any) => ({
            id: m.id,
            text: String(m.text ?? ''),
            sender: m.senderId === myUid ? 'me' : 'them',
            time: m.timestamp?.toDate?.()?.toLocaleTimeString('es-DO', { hour: '2-digit', minute: '2-digit' }) ?? '',
          }))
        );
      },
      (codigo) => {
        // El listener ya NO borra los mensajes al fallar; aquí solo se marca el
        // estado para poder decirle al usuario la verdad en vez de "no hay
        // mensajes aún".
        setCargandoChat(false);
        setErrorChat(codigo);
      }
    );
    return () => { try { (unsub as any)?.(); } catch {} };
  }, [rideId, myUid]);

  const handleSend = async () => {
    // ── POR QUÉ SE LEE DE UN REF Y SE HACE `blur()` ANTES ──────────────────
    //
    // El fundador probó el chat en dos teléfonos: escribió "Dónde está" y al
    // otro lado llegó "Dónde". Se pierden letras del FINAL.
    //
    // No hay ningún recorte en el código: `sendRideMessage` escribe el texto
    // tal cual y las reglas no validan longitud. Lo que sí ocurre en Android es
    // que el teclado mantiene la última palabra en "composición" (subrayada,
    // aún no confirmada). Con un `<TextInput multiline>` controlado, esa
    // palabra puede no haber llegado todavía al estado de React en el momento
    // en que se toca Enviar — y se manda la frase sin ella.
    //
    // Dos cinturones:
    //   1. `blur()` obliga al teclado a CONFIRMAR lo que está componiendo.
    //   2. Se lee de `textoRef`, que se actualiza en cada pulsación y nunca
    //      queda por detrás del estado.
    // El `setTimeout(0)` deja que el commit del teclado llegue antes de leer.
    inputRef.current?.blur();
    await new Promise((r) => setTimeout(r, 0));

    const crudo = textoRef.current ?? message;
    const text = crudo.trim();
    if (!text || sending) return;
    // Si vuelve a pasar, esto lo deja probado en logcat en vez de a ojo.
    if (text.length !== message.trim().length) {
      console.warn(
        `[chat] el teclado tenía texto sin confirmar: estado=${message.trim().length} enviado=${text.length}`
      );
    }
    if (!rideId || !myUid) {
      Alert.alert(
        'Chat no disponible',
        'Este chat se abre desde un viaje activo. Si necesitas ayuda urgente, usa el botón de llamada o el SOS.'
      );
      return;
    }
    alEscribir('');
    setSending(true);
    try {
      await firestoreService.sendRideMessage(rideId, myUid, text);
    } catch (e: any) {
      console.warn('[chat] no se pudo enviar:', e?.message);
      alEscribir(text); // devolvemos el texto para que no lo pierda
      Alert.alert('No se pudo enviar', 'Revisa tu conexión e inténtalo de nuevo.');
    } finally {
      setSending(false);
    }
  };

  // Botón "+" del chat. Antes NO tenía onPress → Google Play rechazó por
  // "Unresponsive UI elements". Ahora envía la ubicación GPS actual como un
  // enlace de Google Maps dentro del mismo hilo del chat (útil real en un
  // viaje: "estoy aquí"). Sin cambiar diseño.
  const handleShareLocation = async () => {
    if (!rideId || !myUid) {
      Alert.alert(
        'Chat no disponible',
        'Este chat se abre desde un viaje activo. Ábrelo desde tu viaje en curso.'
      );
      return;
    }

    // GATE POR ESTADO — compartir tu GPS EN VIVO solo tiene sentido durante un
    // viaje activo. Sin este gate, abrir un chat histórico (viaje ya
    // COMPLETED/CANCELLED) y tocar "+" mandaría tu ubicación real (casa/trabajo)
    // a la otra persona días después. Riesgo de acoso en una app de transporte.
    try {
      const ride = await firestoreService.getRideById(rideId);
      const status = String(ride?.status ?? '').toUpperCase();
      const ACTIVE = ['ACCEPTED', 'ARRIVED', 'IN_PROGRESS'];
      if (!ACTIVE.includes(status)) {
        Alert.alert(
          'Viaje no activo',
          'Solo puedes compartir tu ubicación mientras el viaje está en curso.'
        );
        return;
      }
    } catch (e: any) {
      console.warn('[chat] no se pudo verificar estado del viaje:', e?.message);
      Alert.alert('No disponible', 'No pudimos verificar el estado del viaje. Intenta de nuevo.');
      return;
    }

    // CONSENTIMIENTO EXPLÍCITO — el botón "+" antes estaba muerto; los usuarios
    // lo asocian con "menú de adjuntos". Sin confirmación, un tap accidental (o
    // inducido) filtra la ubicación exacta. Pedimos confirmación antes de emitir.
    Alert.alert(
      'Compartir tu ubicación',
      `Se enviará tu ubicación actual a ${partnerName}. ¿Continuar?`,
      [
        { text: 'Cancelar', style: 'cancel' },
        {
          text: 'Compartir',
          onPress: async () => {
            try {
              const { locationService } = await import('../services/locationService');
              const loc = await locationService.getCurrentLocation();
              if (!loc) {
                Alert.alert('Sin ubicación', 'No pudimos obtener tu GPS ahora mismo. Intenta de nuevo.');
                return;
              }
              const lat = loc.coords.latitude.toFixed(6);
              const lng = loc.coords.longitude.toFixed(6);
              const link = `https://www.google.com/maps?q=${lat},${lng}`;
              await firestoreService.sendRideMessage(rideId, myUid, `📍 Estoy aquí: ${link}`);
            } catch (e: any) {
              console.warn('[chat] compartir ubicación falló:', e?.message);
              Alert.alert('No se pudo compartir', 'Revisa tus permisos de ubicación e inténtalo de nuevo.');
            }
          },
        },
      ]
    );
  };

  const handleCallDriver = () => {
    // Sin número demo. El teléfono real viaja en los params del chat cuando
    // se abre desde un viaje/pedido activo. Si no lo tenemos, guiamos al chat.
    const phone = String((params as any)?.partnerPhone ?? '').trim();
    if (!phone) {
      Alert.alert('Sin número disponible', 'Usa el chat para comunicarte con la otra persona.');
      return;
    }
    const clean = phone.replace(/[\s\-()]/g, '');
    Linking.openURL(`tel:${clean}`).catch((err) => {
      console.error('Call error:', err);
      Alert.alert('Error', 'No se pudo iniciar la llamada telefónica.');
    });
  };

  return (
    // `PantallaSegura` y no `SafeAreaView`: el de react-native es un <View>
    // vacío en Android (@deprecated), y con edge-to-edge activo la barra de
    // escribir quedaba ENTERRADA bajo los tres botones del sistema. El campo
    // existía y se dibujaba, pero el toque se lo llevaba Android — que es
    // exactamente lo que Google reportó como "botones que no responden".
    <PantallaSegura style={[styles.safeArea, { backgroundColor: colors.background }]}>
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        // En Android 'height' SÍ funciona con windowSoftInputMode=adjustResize
        // (que es el que tiene el manifest). Antes iba `undefined`, o sea que
        // al abrir el teclado nada se movía.
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 90 : 0}
      >
        {/* Header */}
        <View style={[styles.header, { borderBottomColor: colors.border, backgroundColor: colors.card }]}>
          <View style={styles.headerLeft}>
            <TouchableOpacity 
              onPress={() => router.canGoBack() ? router.back() : router.replace('/(tabs)/dashboard')} 
              style={[styles.backButton, { zIndex: 10, padding: 10 }]}
            >
              <Ionicons name="arrow-back" size={28} color={colors.primary} />
            </TouchableOpacity>
            <View style={styles.driverInfo}>
              <View style={styles.avatar}>
                <Ionicons name="person" size={20} color={colors.textSecondary} />
              </View>
              <View>
                {/* Antes estaba fijo "Carlos (Conductor)" — un nombre inventado
                    que aparecía en TODOS los chats. Ahora el real. */}
                <Text style={[styles.driverName, { color: colors.text }]}>{partnerName}</Text>
                {/* HONESTIDAD (auditoría) — Antes decía "Cifrado AES-256" y el
                    proyecto NO tiene ninguna librería de criptografía. Declarar
                    cifrado que no existe es motivo de retirada en Play (Data
                    Safety). Lo real: TLS en tránsito + cifrado en reposo. */}
                <Text style={[styles.driverStatus, { color: colors.primary }]}>En línea • Conexión segura</Text>
              </View>
            </View>
          </View>
          {/* Bloquear / reportar. Lo exige la política de contenido de
              usuarios de Google Play en todo chat de uno a uno. */}
          <AccionesDeChat
            otroUid={otroUid}
            otroNombre={partnerName}
            contexto="VIAJE"
            referenciaId={rideId}
            onCambioDeBloqueo={setChatBloqueado}
          />
          <TouchableOpacity onPress={handleCallDriver} style={[styles.callButton, { backgroundColor: 'rgba(76, 175, 80, 0.1)' }]}>
            <Ionicons name="call" size={20} color="#4CAF50" />
          </TouchableOpacity>
        </View>

        {/* Mensaje de Seguridad
            H56 (auditoría 16-sep-2026): decía "Solo tú y el chofer de este
            viaje pueden verlos", y eso no era verdad completa por dos lados:
             · Las reglas de rides/{id}/messages limitan la lectura a los
               CLIENTES pasajero y chofer del viaje; el equipo, con la consola
               o una Cloud Function (Admin SDK), los lee igual. Si la ficha de
               Data Safety declara acceso del personal, este texto la
               contradecía.
             · Esta pantalla la abren los DOS (ActiveRideCard), así que al
               chofer le decía "tú y el chofer". Ahora es neutral. */}
        <View style={styles.securityBanner}>
          <Ionicons name="lock-closed" size={14} color="#666" style={{ marginRight: 6 }} />
          <Text style={styles.securityText}>
            Tus mensajes viajan por conexión segura (TLS) y se guardan cifrados en reposo. En la app solo los ven tú y la otra persona de este viaje; el equipo de Go Safely puede revisarlos por seguridad o soporte.
          </Text>
        </View>

        {/* Chat Area */}
        <ScrollView 
          ref={scrollViewRef}
          contentContainerStyle={[styles.chatContainer, messages.length === 0 && { flex: 1, justifyContent: 'center', alignItems: 'center' }]}
          onContentSizeChange={() => scrollViewRef.current?.scrollToEnd({ animated: true })}
        >
          {/* Tres estados distintos, no uno solo. Antes "cargando", "vacío" y
              "no pudimos leer" mostraban el MISMO texto, así que un fallo de
              permisos se leía como "aquí nunca hubo nada" — y el fundador vio
              desaparecer dos mensajes que sí existían. */}
          {messages.length === 0 && cargandoChat ? (
            <Text style={{ color: colors.textSecondary, textAlign: 'center', fontStyle: 'italic' }}>
              Cargando la conversación…
            </Text>
          ) : messages.length === 0 && errorChat ? (
            <Text style={{ color: colors.error ?? '#FF3B30', textAlign: 'center' }}>
              No pudimos cargar los mensajes. Revisa tu conexión y vuelve a entrar al chat.
            </Text>
          ) : messages.length === 0 ? (
            <Text style={{ color: colors.textSecondary, textAlign: 'center', fontStyle: 'italic' }}>
              No hay mensajes aún. Escribe abajo para iniciar la conversación.
            </Text>
          ) : (
            messages.map((msg) => {
              const isMe = msg.sender === 'me';
              return (
                <View key={msg.id} style={[styles.messageWrapper, isMe ? styles.messageWrapperRight : styles.messageWrapperLeft]}>
                  <View style={[
                    styles.messageBubble, 
                    isMe ? [styles.messageBubbleRight, { backgroundColor: colors.primary }] : [styles.messageBubbleLeft, { backgroundColor: colors.card }]
                  ]}>
                    <Text style={[styles.messageText, { color: isMe ? '#FFF' : colors.text }]}>{msg.text}</Text>
                    <Text style={[styles.messageTime, { color: isMe ? 'rgba(255,255,255,0.7)' : colors.textSecondary }]}>{msg.time}</Text>
                  </View>
                </View>
              );
            })
          )}
        </ScrollView>

        {/* Con la conversación bloqueada no se pinta la caja: dejarla ahí
            sería invitar a escribir un mensaje que el servidor va a denegar. */}
        <AvisoDeBloqueo bloqueado={chatBloqueado} />

        {/* Input Area */}
        {!chatBloqueado && (
        <View style={[styles.inputContainer, { backgroundColor: colors.card, borderTopColor: colors.border }]}>
          <TouchableOpacity style={styles.attachButton} onPress={handleShareLocation}>
            <Ionicons name="add" size={24} color={colors.textSecondary} />
          </TouchableOpacity>
          <View style={[styles.textInputWrapper, { backgroundColor: colors.background }]}>
            <TextInput
              ref={inputRef}
              style={[styles.textInput, { color: colors.text }, Platform.OS === 'web' && { outlineStyle: 'none', borderWidth: 0 } as any]}
              placeholder="Escribe un mensaje..."
              placeholderTextColor={colors.textSecondary}
              value={message}
              onChangeText={alEscribir}
              multiline
              // Mismo tope que las reglas de /rides/{id}/messages (H20).
              maxLength={1000}
            />
          </View>
          <TouchableOpacity style={[styles.sendButton, { backgroundColor: message.trim() ? '#25D366' : colors.textSecondary }]} onPress={handleSend} disabled={!message.trim()}>
            <Ionicons name="send" size={20} color="#FFF" style={{ marginLeft: 3 }} />
          </TouchableOpacity>
        </View>
        )}
      </KeyboardAvoidingView>
    </PantallaSegura>
  );
}

const styles = StyleSheet.create({
  safeArea: { flex: 1 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: SIZES.padding,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  backButton: { padding: 4, marginRight: 8 },
  driverInfo: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(0,0,0,0.05)',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 10,
  },

  driverName: { fontSize: 16, fontWeight: 'bold' },
  driverStatus: { fontSize: 12, fontWeight: '500', marginTop: 2 },
  callButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    justifyContent: 'center',
    alignItems: 'center',
  },
  securityBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFF3E0',
    paddingVertical: 8,
    paddingHorizontal: 16,
  },
  securityText: {
    fontSize: 11,
    color: '#E65100',
    textAlign: 'center',
    fontWeight: '500',
    flex: 1,
  },
  chatContainer: {
    padding: SIZES.padding,
    paddingBottom: 20,
  },
  messageWrapper: {
    width: '100%',
    marginBottom: 12,
    flexDirection: 'row',
  },
  messageWrapperRight: {
    justifyContent: 'flex-end',
  },
  messageWrapperLeft: {
    justifyContent: 'flex-start',
  },
  messageBubble: {
    maxWidth: '80%',
    padding: 12,
    borderRadius: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  messageBubbleLeft: {
    borderBottomLeftRadius: 4,
  },
  messageBubbleRight: {
    borderBottomRightRadius: 4,
  },
  messageText: {
    fontSize: 15,
    lineHeight: 20,
  },
  messageTime: {
    fontSize: 10,
    alignSelf: 'flex-end',
    marginTop: 4,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: SIZES.padding,
    paddingVertical: 10,
    paddingBottom: Platform.OS === 'web' ? 25 : 10,
    borderTopWidth: 1,
  },
  attachButton: {
    padding: 8,
    marginRight: 4,
  },
  textInputWrapper: {
    flex: 1,
    borderRadius: 20,
    minHeight: 40,
    maxHeight: 100,
    justifyContent: 'center',
    paddingHorizontal: 16,
    marginRight: 12,
  },
  textInput: {
    flex: 1,
    fontSize: 15,
    paddingTop: 10,
    paddingBottom: 10,
  },
  sendButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    justifyContent: 'center',
    alignItems: 'center',
  }
});
